import { Connector } from "@web3-react/types";
// import { ethers } from "ethers";
import { getEnv } from "@bkry/bowline-utils";
import { Web3Auth } from "@web3auth/modal";

function parseChainId(chainId) {
  return typeof chainId === "string" ? Number.parseInt(chainId, 16) : chainId;
}

/**
 * @param provider - An EIP-1193 ({@link https://github.com/ethereum/EIPs/blob/master/EIPS/eip-1193.md}) provider.
 * @param onError - Handler to report errors thrown from eventListeners.
 */

export class BowlineWallet extends Connector {
  /** {@inheritdoc Connector.provider} */

  constructor({ actions, options, onError }) {
    super(actions, onError);
    this.clientId = getEnv("REACT_APP_WEB3_AUTH_ID");
    this.userInfo = {};
    this.connector = options.connector;
    this.connection = options.connection;
    this.changeNetwork = this.changeNetwork.bind(this);

    // this.provider = provider;
  }

  /** {@inheritdoc Connector.connectEagerly} */
  async connectEagerly() {
    // await this.activate();
  }

  async setOptions(options) {
    if (options.clientId) this.clientId = options.clientId;
    if (options.connector) this.connector = options.connector;
    if (options.connection) this.connection = options.connection;
    if (options.setStatus) this.setStatus = options.setStatus;
  }

  /** {@inheritdoc Connector.activate} */
  async activate() {
    if (!this.web3auth) {
      await this.startWeb3Auth();
    } else if (this.web3auth.status === "not_ready") {
      await this.web3auth.init();
    } else if (this.web3auth.status === "ready") {
      await this.startProvider();
    } else if (this.web3auth.status === "connected") {
      await this.onConnected({ provider: this.web3auth.provider });
    }

    if (!this.web3auth) {
      return null;
    }
  }

  async deactivate() {
    if (this.web3auth) {
      await this.web3auth.logout();
      this.web3auth = undefined;
    }
    if (this.provider) {
      // this.provider.removeAllListeners();
      this.provider = undefined;
    }
    this.privateKeyProvider = undefined;
    this.userInfo = undefined;
    this.actions.resetState();
  }

  async onReady() {
    console.log("Web3Auth status", this.web3auth.status);
    this.setStatus && this.setStatus(this.web3auth.status);
    await this.startProvider();
  }

  async onNotReady() {
    console.log("Web3Auth status", this.web3auth.status);
    this.setStatus && this.setStatus(this.web3auth.status);
  }

  async onConnecting() {
    console.log("Web3Auth status", this.web3auth.status);
    this.setStatus && this.setStatus(this.web3auth.status);
  }

  async onConnected(data) {
    this.provider = data.provider;
    const cancelActivation = this.actions.startActivation();
    console.log("Web3Auth status", this.web3auth.status);
    this.userInfo = await this.web3auth.getUserInfo();

    return Promise.all([
      data.provider.request({ method: "eth_chainId" }),
      data.provider
        .request({ method: "eth_requestAccounts" })
        .catch(() => data.provider.request({ method: "eth_accounts" })),
    ])
      .then(([chainId, accounts]) => {
        this.actions.update({ chainId: parseChainId(chainId), accounts });
        this.setStatus && this.setStatus(this.web3auth.status);
      })
      .catch((error) => {
        cancelActivation();
        this.setStatus && this.setStatus(this.web3auth.status);

        throw error;
      });
  }

  async onDisconnecting() {
    console.log("Web3Auth status", this.web3auth.status);
    this.setStatus && this.setStatus(this.web3auth.status);
  }

  async onDisconnected() {
    console.log("Web3Auth status", this.web3auth?.status);
    this.setStatus &&
      this.web3auth?.status &&
      this.setStatus(this.web3auth.status);
  }

  async onErrored(error) {
    console.error("Web3Auth encountered an error", error);
    console.log("Web3Auth status", this.web3auth.status);
    this.setStatus && this.setStatus(this.web3auth.status);
  }

  async startWeb3Auth() {
    if (typeof window === "undefined") return null;
    this.web3auth = new Web3Auth({
      clientId: this.clientId,
      web3AuthNetwork: getEnv("REACT_APP_WEB3AUTH_NETWORK")
        ? getEnv("REACT_APP_WEB3AUTH_NETWORK")
        : getEnv("REACT_APP_MAINNET")
        ? "sapphire_mainnet"
        : "testnet",
    });

    this.web3auth.on("not_ready", this.onNotReady.bind(this));
    this.web3auth.on("ready", this.onReady.bind(this));
    this.web3auth.on("connecting", this.onConnecting.bind(this));
    this.web3auth.on("connected", this.onConnected.bind(this));
    this.web3auth.on("disconnecting", this.onDisconnecting.bind(this));
    this.web3auth.on("disconnected", this.onDisconnected.bind(this));
    this.web3auth.on("errored", this.onErrored.bind(this));

    await this.web3auth.init();
  }

  async startProvider() {
    if (this.web3auth && !this.provider) {
      // Ensure web3auth is ready before attempting to connect
      if (
        this.web3auth.status !== "ready" &&
        this.web3auth.status !== "connected"
      ) {
        throw new Error(
          "Web3Auth is not ready yet. Please wait for initialization to complete."
        );
      }

      if (this.web3auth.status === "connected") {
        this.provider = this.web3auth.provider;
      } else {
        const connection = await this.web3auth.connectTo(this.connector, {
          authConnection: this.connection,
        });
        this.provider = connection.provider;
      }
    }
  }

  async changeNetwork(chainId) {
    if (!this.web3auth || this.web3auth.status !== "connected") {
      throw new Error("Web3Auth is not initialized or connected");
    }
    try {
      const parsedChainId = parseChainId(chainId);
      await this.web3auth.switchChain({
        chainId,
      });
      this.actions.update({ chainId: parsedChainId });
    } catch (switchError) {
      console.error("Error switching chain:", switchError);
    }
  }
}
